import { ADMIN_CLASS_USE_MOCK } from "../config/admin.config";
import { errorToast } from "@/utils/constants";
import { showApiErrorToast } from "../utils/apiToastMessage";
import { uploadThumbnailService } from "@/service/base.service";
import Modal from "@/components/design-system/Modal";
import { cn } from "@/utils/utils";
import { useEffect, useRef, useState } from "react";
import { CLASS_LIMITS } from "../constants/class.constants";
import { resolveCropImageSrc } from "../utils/cropImage";
import ThumbnailCropModal from "./ThumbnailCropModal";

const THUMBNAIL_ICONS = {
  upload: "/class-management/icons/thumbnail-upload-icon.svg",
  file: "/class-management/icons/thumbnail-file-icon.svg",
  trash: "/class-management/icons/thumbnail-trash-icon.svg",
} as const;

type UploadPhase = "idle" | "uploading" | "success";

interface ClassThumbnailUploadSectionProps {
  value: string;
  cropSourceUrl?: string;
  uploadId?: string;
  onChange: (url: string) => void;
  onUploadIdChange?: (id: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
  readOnly?: boolean;
  error?: string;
}

const DROPZONE_CLASS =
  "flex w-full max-w-[852px] min-h-[298px] flex-col items-center overflow-hidden rounded-[3px] border border-dashed border-[#E2E8F0] bg-white px-[20px] py-[36px]";

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || "image/png" });
}

async function resolveUploadId(
  dataUrl: string,
  fileName: string
): Promise<{ uploadId: string; previewUrl: string }> {
  if (ADMIN_CLASS_USE_MOCK) {
    return {
      uploadId: `mock-upload-${Date.now()}`,
      previewUrl: dataUrl,
    };
  }

  const file = await dataUrlToFile(dataUrl, fileName);
  const {
    data: { data },
  } = await uploadThumbnailService(file);

  return {
    uploadId: data.id,
    previewUrl: data.preview_url || data.s3_url || dataUrl,
  };
}

export default function ClassThumbnailUploadSection({
  value,
  cropSourceUrl,
  uploadId: _uploadId,
  onChange,
  onUploadIdChange,
  onUploadingChange,
  readOnly = false,
  error,
}: ClassThumbnailUploadSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<UploadPhase>(value ? "success" : "idle");
  const [fileName, setFileName] = useState("image.png");
  const [sourceUrl, setSourceUrl] = useState("");
  const [progress, setProgress] = useState(value ? 100 : 0);
  const [cropOpen, setCropOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [cropImageLoading, setCropImageLoading] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewImageLoading, setPreviewImageLoading] = useState(false);

  useEffect(() => {
    if (value) {
      setPhase("success");
      setProgress(100);
      // Edit: seed proxy crop URL from API. Never use preview_url (value) — S3 breaks re-crop.
      setSourceUrl((prev) => prev || cropSourceUrl || "");
    } else if (!cropOpen) {
      setPhase("idle");
      setProgress(0);
      setSourceUrl("");
      setFileName("image.png");
    }
  }, [value, cropSourceUrl, cropOpen]);

  const cropSource = sourceUrl || cropSourceUrl || "";

  const previewSource =
    sourceUrl.startsWith("data:") || sourceUrl.startsWith("blob:")
      ? sourceUrl
      : value || sourceUrl || cropSourceUrl || "";

  useEffect(() => {
    if (!cropOpen) {
      setModalImageSrc("");
      setCropImageLoading(false);
      return;
    }

    if (!cropSource) return;

    if (cropSource.startsWith("data:") || cropSource.startsWith("blob:")) {
      setModalImageSrc(cropSource);
      return;
    }

    let cancelled = false;
    setCropImageLoading(true);
    setModalImageSrc("");

    void resolveCropImageSrc(cropSource)
      .then((dataUrl) => {
        if (!cancelled) {
          setModalImageSrc(dataUrl);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          showApiErrorToast(error);
          setCropOpen(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCropImageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cropOpen, cropSource]);

  useEffect(() => {
    if (!previewOpen) {
      setPreviewImageSrc("");
      setPreviewImageLoading(false);
      return;
    }

    if (!previewSource) return;

    if (previewSource.startsWith("data:") || previewSource.startsWith("blob:")) {
      setPreviewImageSrc(previewSource);
      return;
    }

    if (/^https?:\/\//i.test(previewSource)) {
      setPreviewImageSrc(previewSource);
      return;
    }

    let cancelled = false;
    setPreviewImageLoading(true);
    setPreviewImageSrc("");

    void resolveCropImageSrc(previewSource)
      .then((dataUrl) => {
        if (!cancelled) {
          setPreviewImageSrc(dataUrl);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          showApiErrorToast(error);
          setPreviewOpen(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewImageLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [previewOpen, previewSource]);

  const openPicker = () => {
    if (readOnly) return;
    inputRef.current?.click();
  };

  const applyUpload = async (dataUrl: string, name: string) => {
    setPhase("uploading");
    setProgress(40);
    onUploadingChange?.(true);
    try {
      const { uploadId: nextId, previewUrl } = await resolveUploadId(dataUrl, name);
      onChange(previewUrl);
      onUploadIdChange?.(nextId);
      // Keep a same-origin data URL for immediate re-crop without CORS issues.
      setSourceUrl(dataUrl);
      setPhase("success");
      setProgress(100);
    } catch (error) {
      setPhase(value ? "success" : "idle");
      setProgress(value ? 100 : 0);
      showApiErrorToast(error);
    } finally {
      onUploadingChange?.(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.type !== "image/png") {
      errorToast("Invalid format", "Supported formats: PNG only");
      return;
    }
    if (file.size > CLASS_LIMITS.thumbnailMaxBytes) {
      errorToast("File too large", "Maximum file size: 25 MB");
      return;
    }

    setFileName(file.name);

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setSourceUrl(dataUrl);
      setCropOpen(true);
    } catch {
      errorToast("Upload failed", "Could not read the image file.");
      setPhase("idle");
      setProgress(0);
    }
  };

  const handleDelete = () => {
    onChange("");
    onUploadIdChange?.("");
    setSourceUrl("");
    setFileName("image.png");
    setPhase("idle");
    setProgress(0);
  };

  const handleCropSave = async (croppedDataUrl: string) => {
    await applyUpload(croppedDataUrl, fileName);
    setCropOpen(false);
  };

  const handleCropCancel = () => {
    setCropOpen(false);
    if (!value) {
      setSourceUrl("");
      setFileName("image.png");
      setProgress(0);
      setPhase("idle");
    }
  };

  const showSuccess = phase === "success" || phase === "uploading" || Boolean(value);

  const handleOpenPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!previewSource) return;
    setPreviewOpen(true);
  };

  return (
    <section>
      <h3 className="mb-1 text-[20px] font-bold leading-[1.5] text-[#1F1F1F]">
        Thumbnail image
      </h3>
      <p className="mb-4 text-[16px] font-medium leading-normal text-[#696A6F]">
        Upload thumbnail images in a 16:9 aspect ratio only. Supported formats:
        PNG. Maximum file size: 25 MB
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={handleFileChange}
        disabled={readOnly}
      />

      <div
        className={cn(
          DROPZONE_CLASS,
          !readOnly && phase === "idle" && "cursor-pointer hover:border-[#5C88C5]"
        )}
        onClick={phase === "idle" && !readOnly ? openPicker : undefined}
        onKeyDown={(e) => {
          if (phase === "idle" && !readOnly && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            openPicker();
          }
        }}
        role={phase === "idle" && !readOnly ? "button" : undefined}
        tabIndex={phase === "idle" && !readOnly ? 0 : undefined}
      >
        {showSuccess ? (
          <div className="flex w-full max-w-[812px] flex-col items-center gap-[15px]">
            <img
              src={THUMBNAIL_ICONS.upload}
              alt=""
              aria-hidden
              className="size-[46px] shrink-0"
            />

            <p className="w-full text-center text-[20px] font-medium leading-[27px] text-[#1F1F1F]">
              {phase === "uploading" ? "Uploading thumbnail…" : "File uploaded successfully"}
            </p>

            {!readOnly ? (
              <p className="w-full text-center text-[12px] font-normal leading-[16px] text-[#696A6F]">
                Delete this file to upload a new file
              </p>
            ) : null}

            <div className="flex h-[48px] w-full items-center justify-between rounded-[10px] bg-[#F9FAFC] p-[4px]">
              <button
                type="button"
                className="flex min-w-0 items-center gap-[5px] rounded-[8px] text-left hover:bg-[#F0F4FA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5C88C5]"
                onClick={handleOpenPreview}
                disabled={!previewSource || phase === "uploading"}
                aria-label={`Preview ${fileName}`}
              >
                <div className="flex size-[40px] shrink-0 items-center justify-center rounded-[10.833px] bg-[#F7FCFF] p-[6px]">
                  <img
                    src={THUMBNAIL_ICONS.file}
                    alt=""
                    aria-hidden
                    className="size-[28px] shrink-0"
                  />
                </div>
                <span className="truncate text-[14px] font-normal leading-[18px] text-[#1E1E1E] underline">
                  {fileName}
                </span>
              </button>

              {!readOnly ? (
                <div className="flex shrink-0 items-center gap-[20px]">
                  <button
                    type="button"
                    className="flex h-[39px] w-[168px] items-center justify-center rounded-[12px] border border-[#2165C5] bg-white px-[40px] py-[10px] text-[14px] font-semibold leading-normal text-[#2165C5] hover:bg-[#F5FBFF]"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCropOpen(true);
                    }}
                  >
                    Crop image
                  </button>
                  <button
                    type="button"
                    className="p-[3px] hover:opacity-80"
                    aria-label="Delete thumbnail"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                  >
                    <img
                      src={THUMBNAIL_ICONS.trash}
                      alt=""
                      aria-hidden
                      className="size-[24px] shrink-0"
                    />
                  </button>
                </div>
              ) : null}
            </div>

            <div className="relative h-[29px] w-full">
              <span className="absolute left-0 top-[8px] -translate-y-1/2 text-[12px] font-medium leading-[16px] text-black">
                {phase === "uploading" ? "Uploading" : "Uploaded"}
              </span>
              <span className="absolute right-0 top-[8px] -translate-y-1/2 text-[12px] font-medium leading-[16px] text-[#1F1F1F]">
                {progress}%
              </span>
              <div className="absolute bottom-[-1px] left-0 right-0 h-[7px] overflow-hidden rounded-[8px] bg-[#E2E8F0]">
                <div
                  className="h-[7px] rounded-[4px] bg-[#5C88C5] transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex w-full max-w-[812px] flex-col items-center justify-center gap-[15px] py-[20px] text-center">
            <img
              src={THUMBNAIL_ICONS.upload}
              alt=""
              aria-hidden
              className="size-[46px]"
            />
            <p className="text-[20px] font-medium leading-[27px] text-[#1F1F1F]">
              Click to upload thumbnail
            </p>
            <p className="text-[12px] font-normal leading-[16px] text-[#696A6F]">
              PNG only, max 25 MB, 16:9
            </p>
          </div>
        )}
      </div>

      {error ? (
        <p className="mt-2 text-[12px] text-[#ef4444]">{error}</p>
      ) : null}

      {cropOpen && !readOnly ? (
        <ThumbnailCropModal
          isOpen={cropOpen}
          imageSrc={modalImageSrc}
          imageLoading={cropImageLoading}
          onClose={handleCropCancel}
          onSave={handleCropSave}
        />
      ) : null}

      {previewOpen ? (
        <Modal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title="Thumbnail preview"
          width="100%"
          maxWidth="896px"
          fitViewport
          className="mx-4"
          contentClassName="p-0"
          titleClassName="text-[16px] font-semibold leading-[28px] text-[#1F1F1F] md:text-[20px]"
        >
          <div className="flex items-center justify-center px-4 py-4 md:px-6 md:pb-6">
            {previewImageLoading || !previewImageSrc ? (
              <p className="py-16 text-[14px] font-medium text-[#696A6F]">
                Loading image…
              </p>
            ) : (
              <img
                src={previewImageSrc}
                alt={fileName}
                className="h-auto max-h-[min(70dvh,540px)] w-full object-contain"
              />
            )}
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
